import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/** The moderator's own note. Staff-facing — no member ever reads it. */
export const VIOLATION_REVIEW_NOTE_MAX_LENGTH = 2000;

const reviewNote = z
  .string()
  .trim()
  .max(VIOLATION_REVIEW_NOTE_MAX_LENGTH)
  .nullable()
  .default(null)
  .transform((note) => (note === null || note === '' ? null : note));

/**
 * A moderator confirming a queued detection (`violations.manage`).
 *
 * **There is no action field, and that is the design.** Confirming applies the
 * violation type's *configured* action, because N20 says a consequence executes
 * "reading the violation-type configuration" — a per-case override would be a
 * second, unconfigured door to suspending and banning, which is exactly what
 * that rule exists to prevent. A moderator who wants a different consequence has
 * two honest routes: change the type (which changes it for everyone, visibly and
 * with an audit trail), or dismiss this violation and act on the **account**
 * through `POST /admin/users/:id/ban`, where the containment check (N28), the
 * last-super-admin guard and the audited status write already live.
 *
 * That is the same split `ReportsAdminController` argues for reports, and it is
 * the reason this endpoint can carry no authority the queue does not already
 * confine.
 */
export const ViolationEnforceSchema = z
  .object({
    note: reviewNote,
    /**
     * The rule this message broke — **only for a row the classifier could not
     * judge at all**, and required there.
     *
     * This is not the action override the docblock above rules out. The action
     * still comes from the type's configuration; what the moderator supplies is
     * the *classification*, which the machine failed to produce (an AI refusal,
     * an exhausted budget). Naming the category is the step that was missed, not
     * a second opinion on the consequence.
     *
     * Ignored for a row that already has a type: a classification the AI did
     * make is not re-openable here, because that would be a per-case choice of
     * consequence wearing a different name.
     */
    violationTypeId: UuidSchema.optional(),
  })
  .strict();

export type ViolationEnforceRequest = z.infer<typeof ViolationEnforceSchema>;

/**
 * A moderator dismissing a queued detection (`violations.manage`).
 *
 * Terminal: the classifier was wrong, or the message is not worth acting on.
 * Nothing happens to the member and nothing is reversed, because a queued
 * detection never had a consequence to reverse.
 */
export const ViolationDismissSchema = z.object({ note: reviewNote }).strict();

export type ViolationDismissRequest = z.infer<typeof ViolationDismissSchema>;
